/**
 * AI Shipment Agent — full DNA match with Overview/Wallet/Orders pages.
 *
 * Uses Vercel AI SDK v6 useChat with DefaultChatTransport.
 * client_turn_id is retained across retries and rotated on success.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, UIMessage } from 'ai';

import ErrorBanner from './components/ErrorBanner';
import type { AiError } from './components/ErrorBanner';
import RateCardList from './components/RateCardList';
import type { ShippingRate } from './components/RateCardList';
import ChargeConfirmCard from './components/ChargeConfirmCard';
import type { ConfirmationRequest } from './components/ChargeConfirmCard';
import FinalShipmentCard from './components/FinalShipmentCard';
import type { ShipmentComplete } from './components/FinalShipmentCard';
import CsvBatchCard from './components/CsvBatchCard';
import ShipmentProgressCard from './components/ShipmentProgressCard';

import orderImportService from '../../services/orderImportService';
import type { ImportParseResult } from '../../services/orderImportService';

import { Page, PageHead, PageTitle, PageSub } from '../../components/ui/Page';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Textarea } from '../../components/ui/Input';
import { Avatar } from '../../components/ui/Avatar';
import { Callout } from '../../components/ui/Callout';
import { Kicker } from '../../components/ui/Kicker';
import { Icon, type IconName } from '../../components/ui/Icon';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') ?? 'http://localhost:8000';

type Suggestion = { label: string; hint: string; icon: IconName; prompt: string };

const SUGGESTIONS: Suggestion[] = [
  {
    label: 'Ship a package',
    hint: 'Weight, route, service — one line',
    icon: 'box',
    prompt: 'Ship a 2 kg box from Ramallah to New York, by Thursday.',
  },
  {
    label: 'Compare rates',
    hint: 'See carriers side by side',
    icon: 'truck',
    prompt: 'Compare shipping rates for a 1 kg parcel from Bethlehem to Los Angeles.',
  },
  {
    label: 'International delivery',
    hint: 'Customs, duties, transit time',
    icon: 'globe',
    prompt: 'I need to send documents to Chicago. What are my options?',
  },
  {
    label: 'Express shipment',
    hint: 'Same-day or next-day priority',
    icon: 'plane',
    prompt: 'What is the fastest way to send a 500g package to Miami today?',
  },
];

type Capability = { icon: IconName; title: string; body: string };

const CAPABILITIES: Capability[] = [
  { icon: 'sparkle', title: 'Parses your brief', body: 'Pulls origin, destination, weight, and service from plain language.' },
  { icon: 'scale', title: 'Quotes in real time', body: 'Aramex, DHL, FedEx, SMSA — live rates against your wallet balance.' },
  { icon: 'shield', title: 'Confirms before charging', body: 'No automatic charges. Every booking needs your explicit approval.' },
  { icon: 'download', title: 'Returns your label', body: 'AWB and PDF ready to print as soon as the charge clears.' },
];

function getToken(): string {
  return localStorage.getItem('token') ?? '';
}

function parseStructured<T>(text: string): T | null {
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object') return parsed as T;
  } catch {
    /* not JSON */
  }
  return null;
}

function parseErrorFromEvent(err: Error): AiError {
  try {
    const jsonStart = err.message.indexOf('{');
    if (jsonStart !== -1) {
      const data = JSON.parse(err.message.slice(jsonStart)) as Record<string, unknown>;
      return {
        status: data.status as number | undefined,
        code: data.code as string | undefined,
        retry_after_seconds: data.retry_after_seconds as number | undefined,
        message: (data.message as string | undefined) ?? err.message,
      };
    }
  } catch {
    /* fall through */
  }
  return { message: err.message };
}

function getMessageText(msg: UIMessage): string {
  if (Array.isArray(msg.parts)) {
    return msg.parts
      .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
      .map((p) => p.text)
      .join('');
  }
  return '';
}

function extractRatesFromMessages(messages: UIMessage[]) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'assistant') continue;
    if (Array.isArray(msg.parts)) {
      for (const part of msg.parts) {
        if (part.type === 'tool-invocation') {
          const inv = (part as any).toolInvocation;
          if (
            inv?.toolName === 'GetShippingRates' &&
            inv?.state === 'result' &&
            inv?.result?.rates
          ) {
            return {
              rates: inv.result.rates as ShippingRate[],
              orderId: (inv.result.order_id as string) ?? '',
            };
          }
        }
      }
    }
    const text = getMessageText(msg);
    if (text) {
      const parsed = parseStructured<{ rates: ShippingRate[]; order_id: string }>(text);
      if (parsed?.rates) return { rates: parsed.rates, orderId: parsed.order_id ?? '' };
    }
  }
  return null;
}

function extractConfirmationRequest(messages: UIMessage[]): ConfirmationRequest | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'assistant') continue;
    const text = getMessageText(msg);
    if (text) {
      const parsed = parseStructured<ConfirmationRequest>(text);
      if (parsed?.type === 'confirmation_request') return parsed;
    }
  }
  return null;
}

function extractShipmentComplete(messages: UIMessage[]): ShipmentComplete | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'assistant') continue;
    const text = getMessageText(msg);
    if (text) {
      const parsed = parseStructured<ShipmentComplete>(text);
      if (parsed?.type === 'shipment_complete') return parsed;
    }
  }
  return null;
}

interface ErrorCard {
  type: 'error_card';
  code?: string;
  message: string;
}

function extractErrorCard(messages: UIMessage[]): ErrorCard | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'assistant') continue;
    const text = getMessageText(msg);
    if (text) {
      const parsed = parseStructured<ErrorCard>(text);
      if (parsed?.type === 'error_card') return parsed;
    }
  }
  return null;
}

interface DraftOrder {
  orderId: string;
  serviceId?: string;
}

/** The most recent CreateDraftOrder tool result in the conversation, if any. */
function extractDraftOrder(messages: UIMessage[]): DraftOrder | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.role !== 'assistant' || !Array.isArray(msg.parts)) continue;
    for (const part of msg.parts) {
      if (part.type === 'tool-invocation') {
        const inv = (part as any).toolInvocation;
        if (inv?.toolName === 'CreateDraftOrder' && inv?.state === 'result' && inv?.result?.order_id) {
          return {
            orderId: inv.result.order_id as string,
            serviceId: (inv.result.service_id as string) || undefined,
          };
        }
      }
    }
  }
  return null;
}

/**
 * True once the conversation already moved past a given draft order — either the assistant
 * replied with a confirmation_request for it, or a rate_selection turn for it was already sent.
 * Guards the auto-chain effect against re-firing for an order that's already progressed.
 */
function hasRespondedToDraft(messages: UIMessage[], draftOrderId: string): boolean {
  let draftMsgIndex = -1;
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (msg.role !== 'assistant' || !Array.isArray(msg.parts)) continue;
    for (const part of msg.parts) {
      if (part.type === 'tool-invocation') {
        const inv = (part as any).toolInvocation;
        if (inv?.toolName === 'CreateDraftOrder' && inv?.state === 'result' && inv?.result?.order_id === draftOrderId) {
          draftMsgIndex = i;
        }
      }
    }
  }
  if (draftMsgIndex === -1) return false;

  for (let i = draftMsgIndex + 1; i < messages.length; i++) {
    const msg = messages[i];
    const text = getMessageText(msg);
    if (!text) continue;
    const parsed = parseStructured<any>(text);
    if (!parsed) continue;
    if (msg.role === 'assistant' && parsed.type === 'confirmation_request' && parsed.order_id === draftOrderId) return true;
    if (msg.role === 'user' && parsed.type === 'rate_selection' && parsed.order_id === draftOrderId) return true;
  }
  return false;
}

/** Known structured JSON payloads the assistant emits — these render as cards, not raw text. */
function isKnownCardPayload(parsed: any): boolean {
  if (!parsed || typeof parsed !== 'object') return false;
  if (Array.isArray(parsed.rates)) return true;
  return (
    parsed.type === 'confirmation_request' ||
    parsed.type === 'shipment_complete' ||
    parsed.type === 'error_card'
  );
}

const MAX_CSV_ROWS = 10;

/** Best-effort client-side data-row count for .csv files (header line excluded). */
function countCsvDataRows(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const lines = text.split(/\r\n|\r|\n/).filter((line) => line.trim().length > 0);
      resolve(Math.max(0, lines.length - 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

function countToolCalls(messages: UIMessage[]): number {
  let count = 0;
  for (const msg of messages) {
    if (msg.role !== 'assistant' || !Array.isArray(msg.parts)) continue;
    for (const part of msg.parts) {
      if (part.type === 'tool-invocation') count += 1;
    }
  }
  return count;
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                     */
/* ------------------------------------------------------------------ */

const TypingIndicator: React.FC = () => (
  <div className="ai-row" style={{ justifyContent: 'flex-start' }}>
    <Avatar style={{ background: 'var(--brand-navy)' }}>
      <Icon name="bot" size={16} color="#fff" />
    </Avatar>
    <div className="ai-bubble ai-bubble-ai ai-typing">
      {[0, 1, 2].map((i) => (
        <span key={i} className="ai-typing-dot" style={{ animationDelay: `${i * 0.18}s` }} />
      ))}
    </div>
  </div>
);

const MessageBubble: React.FC<{ message: UIMessage }> = ({ message }) => {
  const isUser = message.role === 'user';
  const text = getMessageText(message);
  if (!text) return null;
  if (isKnownCardPayload(parseStructured(text))) return null;

  return (
    <div className="ai-row" style={{ justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      {!isUser && (
        <Avatar style={{ background: 'var(--brand-navy)' }}>
          <Icon name="bot" size={16} color="#fff" />
        </Avatar>
      )}
      <div className={`ai-bubble ${isUser ? 'ai-bubble-user' : 'ai-bubble-ai'}`}>{text}</div>
      {isUser && (
        <Avatar style={{ background: 'var(--bg-tint)', color: 'var(--ink-3)' }}>
          <Icon name="user" size={16} />
        </Avatar>
      )}
    </div>
  );
};

const EmptyState: React.FC<{ onPick: (text: string) => void }> = ({ onPick }) => (
  <div className="ai-empty">
    <div className="ai-empty-badge">
      <Icon name="bot" size={28} color="#fff" />
    </div>
    <div className="ai-empty-kicker">
      <Icon name="sparkle" size={11} /> Ready when you are
    </div>
    <h3 className="ai-empty-title">
      Tell me about your <span className="ai-accent">shipment.</span>
    </h3>
    <p className="ai-empty-sub">
      A single sentence is enough — origin, destination, weight. I’ll quote live carriers,
      confirm the charge, and hand back a printable label. You can also attach a CSV of up
      to 10 orders to ship them in one batch.
    </p>
    <div className="ai-empty-chips">
      {SUGGESTIONS.slice(0, 3).map((s) => (
        <button
          key={s.label}
          type="button"
          className="ai-chip"
          onClick={() => onPick(s.prompt)}
        >
          <Icon name={s.icon} size={12} />
          <span>{s.label}</span>
        </button>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Main page                                                          */
/* ------------------------------------------------------------------ */

const AiShipmentPage: React.FC = () => {
  const navigate = useNavigate();
  const clientTurnIdRef = useRef<string>(crypto.randomUUID());
  const conversationIdRef = useRef<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [aiError, setAiError] = useState<AiError | null>(null);
  const [networkError, setNetworkError] = useState(false);
  const [lastUserText, setLastUserText] = useState<string>('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const sessionStartRef = useRef<number>(Date.now());

  const [csvBatch, setCsvBatch] = useState<ImportParseResult | null>(null);
  const [csvParsing, setCsvParsing] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // Auto-chain: order ids we've already auto-sent a rate_selection for (guards against double-fire).
  const autoChainedOrderIdsRef = useRef<Set<string>>(new Set());
  // The last rate the user picked from RateCardList before a draft existed, for the auto-chain fallback.
  const lastPickedRateRef = useRef<{ serviceId: string } | null>(null);

  const chatTransport = useMemo(() => new DefaultChatTransport({
      api: `${API_BASE}/api/ai/chat`,
      headers: () => ({ Authorization: `Bearer ${getToken()}` }),
      fetch: async (input, init) => {
        const response = await globalThis.fetch(input, init);
        const conversationId = response.headers.get('X-Ai-Conversation-Id');
        if (conversationId) conversationIdRef.current = conversationId;
        return response;
      },
      prepareSendMessagesRequest: ({ messages: outgoingMessages, trigger, messageId }) => ({
        body: {
          messages: outgoingMessages,
          trigger,
          messageId,
          client_turn_id: clientTurnIdRef.current,
          conversation_id: conversationIdRef.current ?? undefined,
        },
      }),
    }), []);

  const { messages, sendMessage, status, error: chatError, setMessages } = useChat({
    transport: chatTransport,
    onError: (err: Error) => {
      setNetworkError(true);
      setAiError(parseErrorFromEvent(err));
    },
    onFinish: () => {
      clientTurnIdRef.current = crypto.randomUUID();
      setNetworkError(false);
      setAiError(null);
    },
  });

  useEffect(() => {
    if (chatError) setAiError(parseErrorFromEvent(chatError));
  }, [chatError]);

  const handleScroll = useCallback(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    const threshold = 120;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  }, []);

  useEffect(() => {
    if (isNearBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const isStreaming = status === 'streaming' || status === 'submitted';

  const submitMessage = useCallback(
    (text: string) => {
      if (!text.trim() || isStreaming) return;
      setLastUserText(text);
      setNetworkError(false);
      setAiError(null);
      isNearBottomRef.current = true;
      sendMessage({ role: 'user', parts: [{ type: 'text', text }] });
    },
    [isStreaming, sendMessage]
  );

  const handleSend = () => {
    submitMessage(inputValue);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleRetry = () => {
    setNetworkError(false);
    setAiError(null);
    if (lastUserText)
      sendMessage({ role: 'user', parts: [{ type: 'text', text: lastUserText }] });
  };

  const handleStructuredSend = useCallback(
    (payload: string) => {
      clientTurnIdRef.current = crypto.randomUUID();
      sendMessage({ role: 'user', parts: [{ type: 'text', text: payload }] });
    },
    [sendMessage]
  );

  const handleRateSelect = useCallback(
    (payload: string, rate?: ShippingRate) => {
      if (rate) lastPickedRateRef.current = { serviceId: rate.service_id };
      handleStructuredSend(payload);
    },
    [handleStructuredSend]
  );

  const handleNewChat = () => {
    setMessages([]);
    setInputValue('');
    setAiError(null);
    setNetworkError(false);
    setLastUserText('');
    clientTurnIdRef.current = crypto.randomUUID();
    conversationIdRef.current = null;
    sessionStartRef.current = Date.now();
    setCsvBatch(null);
    setCsvError(null);
  };

  const handleDownloadTemplate = () => {
    orderImportService.downloadTemplate().catch(() => {
      setCsvError('Failed to download the template. Please try again.');
    });
  };

  const processCsvFile = useCallback(async (file: File) => {
    const name = file.name.toLowerCase();
    const isCsv = name.endsWith('.csv');
    const isSpreadsheet = isCsv || name.endsWith('.xlsx') || name.endsWith('.xls');
    if (!isSpreadsheet) {
      setCsvError('Please attach a .csv, .xlsx, or .xls file.');
      return;
    }

    setCsvError(null);

    if (isCsv) {
      try {
        const rowCount = await countCsvDataRows(file);
        if (rowCount > MAX_CSV_ROWS) {
          setCsvError(`This file has ${rowCount} orders — please limit uploads to ${MAX_CSV_ROWS} orders at a time.`);
          return;
        }
      } catch {
        /* fall through to server-side validation if the file can't be read locally */
      }
    }

    setCsvParsing(true);
    try {
      const result = await orderImportService.parseImport(file, { maxRows: MAX_CSV_ROWS });
      setCsvBatch(result);
    } catch (err: any) {
      if (err?.response?.status === 422) {
        setCsvError(
          err?.response?.data?.message ||
            `This file has too many rows or an invalid format — please limit uploads to ${MAX_CSV_ROWS} orders.`
        );
      } else {
        setCsvError(err?.response?.data?.message || err?.message || 'Failed to read the file. Please try again.');
      }
    } finally {
      setCsvParsing(false);
    }
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) processCsvFile(file);
  };

  const handleComposerDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isDraggingFile) setIsDraggingFile(true);
  };

  const handleComposerDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingFile(false);
  };

  const handleComposerDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingFile(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processCsvFile(file);
  };

  const ratesData = extractRatesFromMessages(messages);
  const confirmRequest = extractConfirmationRequest(messages);
  const shipmentComplete = extractShipmentComplete(messages);
  const errorCard = extractErrorCard(messages);
  const draftOrder = extractDraftOrder(messages);

  // Auto-chain: once CreateDraftOrder returns an order_id with no rate_selection/confirmation
  // sent for it yet, automatically send the structured rate_selection turn so the user doesn't
  // have to manually confirm the service twice.
  useEffect(() => {
    if (status !== 'ready') return;
    if (!draftOrder) return;
    if (autoChainedOrderIdsRef.current.has(draftOrder.orderId)) return;
    if (hasRespondedToDraft(messages, draftOrder.orderId)) {
      autoChainedOrderIdsRef.current.add(draftOrder.orderId);
      return;
    }
    const serviceId = draftOrder.serviceId || lastPickedRateRef.current?.serviceId;
    if (!serviceId) return;
    autoChainedOrderIdsRef.current.add(draftOrder.orderId);
    handleStructuredSend(
      JSON.stringify({
        type: 'rate_selection',
        order_id: draftOrder.orderId,
        service_id: serviceId,
        accepted: true,
      })
    );
  }, [status, draftOrder, messages, handleStructuredSend]);

  const userMessageCount = useMemo(
    () => messages.filter((m) => m.role === 'user').length,
    [messages]
  );
  const toolCallCount = useMemo(() => countToolCalls(messages), [messages]);
  const connectionState: { label: string; tone: 'ok' | 'info' | 'warn' } = isStreaming
    ? { label: 'Working…', tone: 'info' }
    : aiError || chatError
      ? { label: 'Error', tone: 'warn' }
      : { label: 'Ready', tone: 'ok' };
  const sessionState: { label: string; tone: 'ok' | 'info' | 'warn' } = shipmentComplete
    ? { label: 'Shipment booked', tone: 'ok' }
    : confirmRequest
      ? { label: 'Awaiting confirm', tone: 'warn' }
      : ratesData
        ? { label: 'Reviewing rates', tone: 'info' }
        : isStreaming
          ? { label: 'Thinking…', tone: 'info' }
          : messages.length > 0
            ? { label: 'In progress', tone: 'info' }
            : { label: 'Ready', tone: 'ok' };

  return (
    <Page width="wide">
      <PageHead>
        <div>
          <Kicker>
            <Icon name="sparkle" size={11} /> AI assistant · Beta
          </Kicker>
          <PageTitle style={{ marginTop: 8 }}>
            AI shipment <span className="ai-accent">agent.</span>
          </PageTitle>
          <PageSub>
            Describe your parcel in plain language — we’ll quote live carriers, confirm the
            charge, and return a ready-to-print label.
          </PageSub>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <Button
            variant="ghost"
            size="md"
            onClick={handleNewChat}
            disabled={messages.length === 0 || isStreaming}
            title="Start a fresh conversation"
          >
            <Icon name="refresh" size={14} />
            <span>New chat</span>
          </Button>
          <Button variant="blue" size="md" onClick={() => document.getElementById('ai-composer-input')?.focus()}>
            <Icon name="send" size={14} />
            <span>Focus composer</span>
          </Button>
        </div>
      </PageHead>

      {/* ——— Stat row ——— */}
      <section className="ai-stats">
        <div className="ai-stat">
          <div className="ai-stat-label">Session</div>
          <div className="ai-stat-row">
            <div className="ai-stat-value">{sessionState.label}</div>
            <span className={`ai-pulse tone-${sessionState.tone}`}>
              <span className="ai-pulse-dot" />
            </span>
          </div>
          <div className="ai-stat-foot">Assistant is online and responsive.</div>
        </div>

        <div className="ai-stat">
          <div className="ai-stat-label">Messages</div>
          <div className="ai-stat-row">
            <div className="ai-stat-value mono">{messages.length}</div>
            <span className="ai-delta ai-delta-info">{userMessageCount} you</span>
          </div>
          <div className="ai-stat-foot">Across this conversation.</div>
        </div>

        <div className="ai-stat">
          <div className="ai-stat-label">Tools invoked</div>
          <div className="ai-stat-row">
            <div className="ai-stat-value mono">{toolCallCount}</div>
            <span className="ai-delta ai-delta-info">live</span>
          </div>
          <div className="ai-stat-foot">Rate quotes, charges, label calls.</div>
        </div>

        <div className="ai-stat">
          <div className="ai-stat-label">Model</div>
          <div className="ai-stat-row">
            <div className="ai-stat-value" style={{ fontSize: 22 }}>Almaship AI</div>
          </div>
          <div className="ai-stat-foot">Shipping-tuned, tool-calling.</div>
        </div>
      </section>

      {/* ——— Main split: chat + companion ——— */}
      <section className="ai-grid">
        <Card className="ai-chat-card">
          <div className="ov-card-head">
            <div>
              <div className="kicker">Conversation</div>
              <div className="ov-card-title">Shipment assistant</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className={`ai-online tone-${connectionState.tone}`}>
                <span className="ai-online-dot" />
                {connectionState.label}
              </span>
              <Button variant="ghost" size="icon" onClick={handleNewChat} title="Clear conversation" disabled={messages.length === 0}>
                <Icon name="trash" size={14} />
              </Button>
            </div>
          </div>

          {aiError && (
            <div style={{ padding: '12px 20px 0' }}>
              <ErrorBanner error={aiError} onDismiss={() => setAiError(null)} />
            </div>
          )}
          {networkError && !aiError && (
            <div style={{ padding: '12px 20px 0' }}>
              <Callout tone="warn">
                <Icon name="alertCircle" size={16} />
                <span>Connection interrupted. Click retry to resend.</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  onClick={handleRetry}
                  style={{ marginInlineStart: 'auto' }}
                >
                  <Icon name="refresh" size={12} /> Retry
                </button>
              </Callout>
            </div>
          )}

          <div
            ref={scrollAreaRef}
            onScroll={handleScroll}
            role="log"
            aria-live="polite"
            className="ai-messages"
          >
            {messages.length === 0 && !csvBatch ? (
              <EmptyState onPick={submitMessage} />
            ) : (
              <>
                {messages.map((msg) => (
                  <MessageBubble key={msg.id} message={msg} />
                ))}

                {ratesData && !shipmentComplete && !confirmRequest && (
                  <div style={{ marginTop: 8 }}>
                    <RateCardList
                      rates={ratesData.rates}
                      orderId={draftOrder?.orderId ?? ''}
                      onSelect={handleRateSelect}
                      disabled={isStreaming}
                    />
                  </div>
                )}
                {confirmRequest && !shipmentComplete && (
                  <div style={{ marginTop: 8 }}>
                    <ChargeConfirmCard
                      request={confirmRequest}
                      onConfirm={handleStructuredSend}
                      onCancel={handleStructuredSend}
                      disabled={isStreaming}
                    />
                  </div>
                )}
                {shipmentComplete && (
                  <div style={{ marginTop: 8 }}>
                    <FinalShipmentCard
                      shipment={shipmentComplete}
                      onViewOrder={
                        shipmentComplete.order_id
                          ? () => navigate(`/orders/${shipmentComplete.order_id}`)
                          : undefined
                      }
                    />
                  </div>
                )}
                {errorCard && !shipmentComplete && (
                  <div style={{ marginTop: 8 }}>
                    <ErrorBanner error={{ code: errorCard.code, message: errorCard.message }} />
                  </div>
                )}
                {csvBatch && (
                  <div style={{ marginTop: 8 }}>
                    <CsvBatchCard result={csvBatch} onDiscard={() => setCsvBatch(null)} />
                  </div>
                )}
              </>
            )}

            {isStreaming && <TypingIndicator />}
            <div ref={bottomRef} />
          </div>

          <div
            className={`ai-composer ${isDraggingFile ? 'ai-composer-dragging' : ''}`}
            onDragOver={handleComposerDragOver}
            onDragLeave={handleComposerDragLeave}
            onDrop={handleComposerDrop}
          >
            {csvError && (
              <Callout tone="warn" style={{ marginBottom: 10 }}>
                <Icon name="alertCircle" size={16} />
                <span style={{ flex: 1 }}>{csvError}</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  onClick={() => setCsvError(null)}
                  aria-label="dismiss"
                >
                  <Icon name="x" size={12} />
                </button>
              </Callout>
            )}
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />
            <div className="ai-composer-inner">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => csvInputRef.current?.click()}
                disabled={csvParsing}
                title="Attach a CSV/XLSX of up to 10 orders"
                aria-label="Attach order sheet"
              >
                <Icon name="paperclip" size={16} />
              </Button>
              <Textarea
                id="ai-composer-input"
                rows={1}
                placeholder="Describe your shipment… or attach a CSV of up to 10 orders"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isStreaming}
              />
              <Button
                variant="blue"
                size="md"
                onClick={handleSend}
                disabled={isStreaming || !inputValue.trim()}
                aria-label="Send"
              >
                <Icon name="send" size={14} />
                <span>Send</span>
              </Button>
            </div>
            <div className="ai-composer-foot">
              <span>
                {csvParsing ? 'Reading file…' : (
                  <>
                    <kbd>Enter</kbd> to send · <kbd>Shift</kbd> + <kbd>Enter</kbd> for new line
                  </>
                )}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                <button type="button" className="ai-template-link" onClick={handleDownloadTemplate}>
                  <Icon name="download" size={11} /> Order template
                </button>
                <span>AI can make mistakes — review before confirming.</span>
              </span>
            </div>
          </div>
        </Card>

        {/* ——— Companion column ——— */}
        <div className="ai-side">
          <ShipmentProgressCard messages={messages} shipmentComplete={!!shipmentComplete} />

          <Card className="ai-side-card">
            <div className="ov-card-head">
              <div>
                <div className="kicker">Try asking</div>
                <div className="ov-card-title">Quick prompts</div>
              </div>
            </div>
            <div className="ov-quick-grid">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  className="ov-quick-item"
                  onClick={() => submitMessage(s.prompt)}
                  disabled={isStreaming}
                >
                  <div className="ov-quick-icon">
                    <Icon name={s.icon} size={15} />
                  </div>
                  <div>
                    <div className="ov-quick-title">{s.label}</div>
                    <div className="ov-quick-sub">{s.hint}</div>
                  </div>
                  <Icon name="arrowRight" size={13} color="var(--ink-4)" />
                </button>
              ))}
            </div>
          </Card>

          <Card className="ai-side-card">
            <div className="ov-card-head">
              <div>
                <div className="kicker">How it works</div>
                <div className="ov-card-title">Capabilities</div>
              </div>
            </div>
            <div className="ai-caps">
              {CAPABILITIES.map((c) => (
                <div key={c.title} className="ai-cap">
                  <div className="ai-cap-icon">
                    <Icon name={c.icon} size={14} />
                  </div>
                  <div>
                    <div className="ai-cap-title">{c.title}</div>
                    <div className="ai-cap-body">{c.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </section>

      <AiPageStyles />
    </Page>
  );
};

/* ------------------------------------------------------------------ */
/* Inline CSS — scoped to the AI page                                 */
/* ------------------------------------------------------------------ */

const AiPageStyles: React.FC = () => (
  <style>{`
    .ai-accent {
      font-family: var(--font-disp);
      font-style: italic;
      font-weight: 400;
      color: var(--brand-blue);
      letter-spacing: -0.01em;
    }

    /* ——— Stats row ——— */
    .ai-stats {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 14px;
      margin-bottom: 20px;
    }
    .ai-stat {
      background: var(--bg-elev);
      border: 1px solid var(--line);
      border-radius: var(--r-lg);
      padding: 18px 20px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      transition: border-color 0.15s;
    }
    .ai-stat:hover { border-color: var(--line-strong); }
    .ai-stat-label {
      font-size: 12px;
      font-weight: 500;
      color: var(--ink-3);
    }
    .ai-stat-row {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 12px;
    }
    .ai-stat-value {
      font-size: 26px;
      font-weight: 600;
      letter-spacing: -0.02em;
      color: var(--ink);
      line-height: 1;
      font-variant-numeric: tabular-nums;
    }
    .ai-stat-foot { font-size: 12px; color: var(--ink-3); padding-top: 2px; }

    .ai-pulse {
      width: 28px; height: 28px; border-radius: 999px;
      display: inline-flex; align-items: center; justify-content: center;
      position: relative;
    }
    .ai-pulse-dot {
      width: 10px; height: 10px; border-radius: 999px;
      background: currentColor;
      box-shadow: 0 0 0 0 currentColor;
      animation: aiPulse 2.2s ease-out infinite;
    }
    .ai-pulse.tone-ok   { color: var(--ok); }
    .ai-pulse.tone-info { color: var(--brand-blue); }
    .ai-pulse.tone-warn { color: var(--warn); }
    @keyframes aiPulse {
      0%   { box-shadow: 0 0 0 0 currentColor; opacity: 1; }
      70%  { box-shadow: 0 0 0 10px transparent; opacity: 0.5; }
      100% { box-shadow: 0 0 0 0 transparent; opacity: 1; }
    }

    .ai-delta {
      font-size: 11px; font-weight: 600;
      padding: 2px 8px; border-radius: 999px;
      font-variant-numeric: tabular-nums;
      background: var(--bg-tint); color: var(--ink-2);
    }
    .ai-delta-info { background: var(--info-bg); color: var(--info); }

    /* ——— Main grid ——— */
    .ai-grid {
      display: grid;
      grid-template-columns: 1.55fr 1fr;
      gap: 16px;
      align-items: stretch;
    }

    .ai-chat-card {
      display: flex;
      flex-direction: column;
      min-height: calc(100vh - 340px);
      overflow: hidden;
    }

    /* ——— Online pill ——— */
    .ai-online {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 4px 10px;
      background: color-mix(in srgb, currentColor 12%, transparent);
      color: var(--ok);
      border: 1px solid color-mix(in srgb, currentColor 30%, transparent);
      border-radius: 999px;
      font-size: 11px; font-weight: 600;
      letter-spacing: 0.06em; text-transform: uppercase;
    }
    .ai-online.tone-info { color: var(--brand-blue); }
    .ai-online.tone-warn { color: var(--warn); }
    .ai-online-dot {
      width: 6px; height: 6px; border-radius: 999px;
      background: currentColor;
      animation: aiPulse 2.2s ease-out infinite;
    }

    /* ——— Messages ——— */
    .ai-messages {
      flex: 1;
      overflow-y: auto;
      padding: 20px 22px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      background:
        radial-gradient(circle at 8% 0%, color-mix(in srgb, var(--brand-cyan) 6%, transparent), transparent 40%),
        var(--bg);
    }
    .ai-row {
      display: flex;
      gap: 10px;
      align-items: flex-end;
    }
    .ai-bubble {
      padding: 10px 14px;
      font-size: 14px;
      line-height: 1.55;
      max-width: 72%;
      white-space: pre-wrap;
      word-break: break-word;
      border-radius: 14px 14px 14px 4px;
    }
    .ai-bubble-ai {
      background: var(--bg-elev);
      border: 1px solid var(--line);
      color: var(--ink);
    }
    .ai-bubble-user {
      background: linear-gradient(135deg, var(--brand-navy), var(--brand-blue));
      color: #fff;
      border-radius: 14px 14px 4px 14px;
      box-shadow: 0 6px 16px rgba(17, 26, 61, 0.18);
    }
    .ai-typing {
      display: inline-flex;
      align-items: center;
      gap: 5px;
    }
    .ai-typing-dot {
      width: 6px; height: 6px; border-radius: 999px;
      background: var(--ink-3);
      opacity: 0.5;
      animation: aiDot 1.2s ease-in-out infinite;
    }
    @keyframes aiDot {
      0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
      40%           { transform: scale(1);   opacity: 1; }
    }

    /* ——— Empty state ——— */
    .ai-empty {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 36px 20px;
    }
    .ai-empty-badge {
      width: 72px; height: 72px;
      border-radius: 20px;
      display: flex; align-items: center; justify-content: center;
      background:
        radial-gradient(circle at 30% 30%, rgba(25, 198, 230, 0.85), transparent 60%),
        linear-gradient(135deg, var(--brand-navy), var(--brand-blue));
      color: #fff;
      box-shadow: 0 12px 32px rgba(17, 26, 61, 0.25);
      margin-bottom: 22px;
      position: relative;
    }
    .ai-empty-badge::after {
      content: ""; position: absolute; inset: -8px;
      border-radius: 24px;
      border: 1px dashed color-mix(in srgb, var(--brand-blue) 40%, transparent);
      animation: aiSpin 18s linear infinite;
    }
    @keyframes aiSpin { to { transform: rotate(360deg); } }

    .ai-empty-kicker {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 4px 10px;
      background: var(--bg-tint);
      color: var(--ink-2);
      border: 1px solid var(--line);
      border-radius: 999px;
      font-size: 11px; font-weight: 600;
      letter-spacing: 0.08em; text-transform: uppercase;
      margin-bottom: 12px;
    }
    .ai-empty-title {
      font-size: 26px;
      font-weight: 600;
      letter-spacing: -0.02em;
      color: var(--ink);
      margin: 0 0 8px;
    }
    .ai-empty-sub {
      font-size: 14px;
      color: var(--ink-3);
      max-width: 420px;
      line-height: 1.6;
      margin: 0 0 20px;
    }
    .ai-empty-chips {
      display: flex; flex-wrap: wrap; gap: 8px; justify-content: center;
    }
    .ai-chip {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 7px 12px;
      background: var(--bg-elev);
      border: 1px solid var(--line);
      border-radius: 999px;
      font-size: 12px; font-weight: 500;
      color: var(--ink-2);
      cursor: pointer;
      font-family: inherit;
      transition: background 0.12s, border-color 0.12s, transform 0.12s;
    }
    .ai-chip:hover {
      background: var(--bg-tint);
      border-color: var(--line-strong);
      color: var(--ink);
      transform: translateY(-1px);
    }

    /* ——— Composer ——— */
    .ai-composer {
      border-top: 1px solid var(--line);
      background: var(--bg-elev);
      padding: 14px 18px 12px;
      transition: background 0.12s;
    }
    .ai-composer-dragging {
      background: color-mix(in srgb, var(--brand-blue) 8%, var(--bg-elev));
    }
    .ai-composer-dragging .ai-composer-inner {
      border-color: var(--brand-blue);
      border-style: dashed;
    }
    .ai-template-link {
      display: inline-flex; align-items: center; gap: 4px;
      background: none; border: none; padding: 0;
      font-family: inherit; font-size: 11px; font-weight: 600;
      color: var(--ink-3);
      cursor: pointer;
    }
    .ai-template-link:hover { color: var(--brand-blue); }
    .ai-composer-inner {
      display: flex;
      gap: 10px;
      align-items: flex-end;
      padding: 8px 8px 8px 14px;
      background: var(--bg);
      border: 1px solid var(--line);
      border-radius: 14px;
      transition: border-color 0.12s, box-shadow 0.12s;
    }
    .ai-composer-inner:focus-within {
      border-color: var(--brand-blue);
      box-shadow: var(--shd-focus);
    }
    .ai-composer-inner textarea {
      border: none !important;
      resize: none;
      font-size: 14px;
      min-height: 28px;
      max-height: 140px;
      background: transparent !important;
      padding: 6px 0 !important;
      box-shadow: none !important;
    }
    .ai-composer-inner textarea:focus { box-shadow: none !important; }
    .ai-composer-foot {
      display: flex; align-items: center; justify-content: space-between;
      margin-top: 8px; padding: 0 4px;
      font-size: 11px; color: var(--ink-4);
      flex-wrap: wrap; gap: 6px;
    }
    .ai-composer-foot kbd {
      font-family: var(--font-mono);
      font-size: 10px;
      padding: 1px 5px;
      background: var(--bg-tint);
      border: 1px solid var(--line);
      border-bottom-width: 2px;
      border-radius: 4px;
      color: var(--ink-2);
    }

    /* ——— Companion column ——— */
    .ai-side { display: flex; flex-direction: column; gap: 16px; }
    .ai-side-card { display: flex; flex-direction: column; }

    .ai-caps {
      padding: 8px 14px 16px;
      display: flex; flex-direction: column;
    }
    .ai-cap {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 12px;
      padding: 12px 6px;
      border-bottom: 1px dashed var(--line);
    }
    .ai-cap:last-child { border-bottom: none; }
    .ai-cap-icon {
      width: 30px; height: 30px; border-radius: 8px;
      background: linear-gradient(135deg, var(--brand-navy), var(--brand-blue));
      color: var(--brand-cyan);
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .ai-cap-title {
      font-size: 13px; font-weight: 600; color: var(--ink);
    }
    .ai-cap-body {
      font-size: 12px; color: var(--ink-3); margin-top: 2px; line-height: 1.5;
    }

    .ov-quick-item[disabled] { opacity: 0.5; cursor: not-allowed; }

    /* ——— Responsive ——— */
    @media (max-width: 1200px) {
      .ai-stats { grid-template-columns: repeat(2, 1fr); }
      .ai-grid { grid-template-columns: 1fr; }
      .ai-chat-card { min-height: 560px; }
    }
    @media (max-width: 760px) {
      .ai-stats { grid-template-columns: 1fr 1fr; }
      .ai-bubble { max-width: 85%; }
      .ai-empty-title { font-size: 22px; }
    }
  `}</style>
);

export default AiShipmentPage;
